import React, { useState, useCallback } from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import Video from 'react-native-video';

interface MediaVideoProps {
    /** Video URL (MP4 H.264 + AAC, served from MinIO content bucket or external) */
    url: string;
    /** Accessibility label / display label for the video */
    label?: string;
    /** Optional poster image URL (shown before play) */
    poster?: string;
}

/**
 * MediaVideo — student-app video player wrapping react-native-video.
 *
 * Design rules (rules/09, rules/02):
 *  - Tap-to-play (never autoplay) — matches the existing audio opt-in pattern
 *  - Large touch targets (>=56px play button)
 *  - Gentle error handling ("Let's try again!") — never a red error screen,
 *    never a silent catch
 *  - Accessibility labels for screen reader support
 *
 * WP-056: Video Playback Platform Upgrade
 */
export const MediaVideo: React.FC<MediaVideoProps> = ({ url, label, poster }) => {
    const [isPlaying, setIsPlaying] = useState(false);
    const [hasError, setHasError] = useState(false);

    const handlePlay = useCallback(() => {
        if (hasError) {
            setHasError(false);
        }
        setIsPlaying(true);
    }, [hasError]);

    const handleRetry = useCallback(() => {
        setHasError(false);
        setIsPlaying(false);
    }, []);

    const handleError = useCallback(() => {
        // Gentle error handling — surface a kind, visible message.
        // Never a silent catch (rules/02), never a scary red screen (rules/09).
        setHasError(true);
        setIsPlaying(false);
    }, []);

    if (hasError) {
        return (
            <View style={styles.container}>
                <View style={styles.errorPill}>
                    <Text style={styles.errorText} allowFontScaling={true} maxFontSizeMultiplier={1.5}>
                        Let&apos;s try again!
                    </Text>
                    <TouchableOpacity
                        testID="video-retry-btn"
                        style={styles.retryBtn}
                        onPress={handleRetry}
                        accessibilityLabel="Retry video"
                        accessibilityRole="button"
                        accessibilityHint="Double tap to try playing the video again"
                    >
                        <Text style={styles.retryIcon} allowFontScaling={true} maxFontSizeMultiplier={1.5}>↻</Text>
                    </TouchableOpacity>
                </View>
            </View>
        );
    }

    return (
        <View style={styles.container}>
            <Video
                source={{ uri: url }}
                style={styles.video}
                controls={isPlaying}
                paused={!isPlaying}
                resizeMode="contain"
                poster={poster}
                onError={handleError}
                accessibilityLabel={label || 'Video player'}
                accessibilityRole="image"
            />
            {!isPlaying && (
                <TouchableOpacity
                    testID="video-play-btn"
                    style={styles.playBtn}
                    onPress={handlePlay}
                    accessibilityLabel={label || 'Play video'}
                    accessibilityRole="button"
                    accessibilityHint="Double tap to play the video"
                >
                    <Text style={styles.playIcon} allowFontScaling={true} maxFontSizeMultiplier={1.5}>▶</Text>
                </TouchableOpacity>
            )}
            {label && !isPlaying && (
                <Text style={styles.label} allowFontScaling={true} maxFontSizeMultiplier={1.5}>{label}</Text>
            )}
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        borderRadius: 12,
        overflow: 'hidden',
        backgroundColor: '#EFF6FF',
        borderWidth: 1,
        borderColor: '#93C5FD',
        padding: 12,
        gap: 8,
    },
    playBtn: {
        minWidth: 56,
        minHeight: 56,
        borderRadius: 28,
        backgroundColor: '#2563EB',
        alignItems: 'center',
        justifyContent: 'center',
        alignSelf: 'center',
    },
    playIcon: {
        fontSize: 24,
        color: '#fff',
    },
    label: {
        fontSize: 15,
        fontWeight: '600',
        color: '#1D4ED8',
        textAlign: 'center',
    },
    video: {
        width: '100%',
        height: 220,
        borderRadius: 8,
    },
    errorPill: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 12,
        padding: 8,
    },
    errorText: {
        fontSize: 16,
        fontWeight: '600',
        color: '#D97706',
    },
    retryBtn: {
        minWidth: 56,
        minHeight: 56,
        borderRadius: 28,
        backgroundColor: '#FEF3C7',
        alignItems: 'center',
        justifyContent: 'center',
    },
    retryIcon: {
        fontSize: 24,
        color: '#D97706',
    },
});
